import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Tab,
  Tabs,
  Paper,
  Snackbar,
  Alert,
  Container,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import AddIcon from "@mui/icons-material/Add";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router-dom";

import PostNewJOB from "../components/PostNewJob";
import ManageJobBoard from "../components/ManageJobBoard";
import CandidateTrackingPage from "../components/CandidateTrackingPage";

const HRDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    navigate("/login");
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const tabItems = [
    { label: "Manage Jobs", icon: <BusinessCenterIcon fontSize="small" /> },
    { label: "Add Job", icon: <AddIcon fontSize="small" /> },
    { label: "Candidates", icon: <PeopleIcon fontSize="small" /> },
    { label: "Reports", icon: <BarChartIcon fontSize="small" /> },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 0:
        return  <ManageJobBoard /> ;
      case 1:
        return <PostNewJOB />;
      case 2:
        return <CandidateTrackingPage />;
      case 3:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom color="primary.main">
              HR Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This section will display HR analytics and reports.
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      {/* Main AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "#fff",
          color: "text.primary",
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Container>
          <Toolbar disableGutters sx={{ height: 60 }}>
            {isMobile && (
              <IconButton 
                edge="start" 
                color="inherit" 
                onClick={() => setDrawerOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              SmartHire
            </Typography>
            
            <Box sx={{ ml: "auto" }}>
              <Button
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                size="small"
                sx={{
                  color: "text.secondary",
                  textTransform: "none",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Logout
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Fixed Tab Navigation for desktop */}
      {!isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            top: 60,
            backgroundColor: "#fff",
            zIndex: theme.zIndex.drawer,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Container>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ 
                minHeight: 48,
                "& .MuiTab-root": {
                  minHeight: 48,
                  textTransform: "none",
                  fontSize: "0.95rem",
                }
              }}
            >
              {tabItems.map((item, index) => (
                <Tab 
                  key={index} 
                  icon={item.icon} 
                  label={item.label} 
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Container>
        </AppBar>
      )}

      {/* Drawer for mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen && isMobile}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 250, pt: 2 }}>
          <List>
            {tabItems.map((item, index) => (
              <ListItemButton
                key={index}
                selected={activeTab === index}
                onClick={(e) => handleTabChange(e, index)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content - adjusted padding top to account for fixed tabs */}
      <Container sx={{ pt: isMobile ? 9 : 15, pb: 4 }}>
        {/* Content panel */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 1,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            backgroundColor: "#fff",
            minHeight: "500px",
          }}
        >
          {renderContent()}
        </Paper>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="success" 
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HRDashboard;